import * as React from 'react';

/**
 * The navigation icon set (handoff "Icon set"): every glyph the rail, the account menu and the trail
 * draw, as the handoff's exact path strings. viewBox 0 0 24 24, fill none, stroke currentColor, round
 * caps and joins. Stroke is 1.8 idle and 2 when active unless a caller says otherwise.
 *
 * ⛔ ONE-LINE STRINGS, byte-identical to the handoff. Do not wrap or reformat them: a gate greps for
 * each one, and a re-wrapped string is a silently different icon.
 */
export const NAV_ICON_PATH = {
  dashboard: 'M3 3h7v9H3z M14 3h7v5h-7z M14 12h7v9h-7z M3 16h7v5H3z',
  models: 'M12 3 3 8l9 5 9-5-9-5z M3 13l9 5 9-5 M3 17.5l9 5 9-5',
  back: 'M19 12H5 M12 19l-7-7 7-7',
  info: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z M12 16v-4 M12 8h.01',
  folder: 'M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z',
  flask: 'M9 3h6 M10 3v6L4 19a2 2 0 0 0 2 3h12a2 2 0 0 0 2-3l-6-10V3 M7 15h10',
  toolbox: 'M3 8h18v12H3z M8 8V5h8v3 M3 13h18',
  lock: 'M16 11V7a4 4 0 0 0-8 0v4 M5 11h14v10H5z',
  user: 'M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2 M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z',
  settings: 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.9.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.9 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.9l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.9.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.9-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.9V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z',
  signOut: 'M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4 M16 17l5-5-5-5 M21 12H9',
  chevronRight: 'M9 6l6 6-6 6',
  chevronsUpDown: 'M8 9l4-4 4 4 M8 15l4 4 4-4',
} as const;

export type NavIconName = keyof typeof NAV_ICON_PATH;

/**
 * One glyph. `size` is a CSS length: the default `1em` lets the host set the pixel size with a
 * `font-size` (the rail's rows set 20px), and a caller with a fixed size passes it (the trail's
 * chevron is 12, the account row's 16).
 */
export function NavIcon(props: { name: NavIconName; size?: number | string; strokeWidth?: number; style?: React.CSSProperties }) {
  const { name, size = '1em', strokeWidth = 1.8, style } = props;
  return (
    <svg
      aria-hidden
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ flex: 'none', ...style }}
    >
      <path d={NAV_ICON_PATH[name]} />
    </svg>
  );
}

const iconOf = (name: NavIconName) => function NavGlyph() { return <NavIcon name={name} />; };

/** Icons as components, for callers that take an `ElementType` (the phone tab bar's `Icon`). */
export const DashboardIcon = iconOf('dashboard');
export const ModelsIcon = iconOf('models');
export const InfoIcon = iconOf('info');
export const FolderIcon = iconOf('folder');
export const FlaskIcon = iconOf('flask');
export const ToolboxIcon = iconOf('toolbox');
export const LockIcon = iconOf('lock');
